import { useCallback, useEffect, useState } from "react";
import { Eye, Pencil, Users, X } from "lucide-react";
import { useAuth } from "../../services/AuthContext";
import {
  getAdminUserBilling,
  getAdminUsers,
  resetAdminUserAccess,
  updateAdminUserEmail,
} from "../../services/admin";
import {
  formatBRL,
  formatDateBr,
  formatDateTimeBr,
  SUBSCRIPTION_STATUS_LABELS,
} from "../../config/admin";
import AdminPagination from "./AdminPagination";
import AdminReloadOverlay from "./AdminReloadOverlay";

const PAGE_SIZE = 20;
const defaultPhoto =
  "https://dominio-eletrico-profile-images-dev.s3.us-east-1.amazonaws.com/default_user_image.jpg";

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());

const PAYMENT_LABELS = {
  RECEIVED: "Recebido",
  CONFIRMED: "Confirmado",
  PENDING: "Pendente",
  OVERDUE: "Vencido",
};

const CYCLE_LABELS = {
  MONTHLY: "Mensal",
  YEARLY: "Anual",
  QUINQUENNIAL: "Quinquenal",
};

const BILLING_LABELS = {
  PIX: "PIX",
  CREDIT_CARD: "Cartão",
};

const statusClass = (status) => {
  const key = String(status || "").toLowerCase();
  return `admin-status admin-status-${key}`;
};

export default function AdminUsersPage() {
  const { getIdToken } = useAuth();
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState({ offset: 0, total: 0, has_next: false, has_prev: false });
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [accountType, setAccountType] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailsUser, setDetailsUser] = useState(null);
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState("");
  const [emailValue, setEmailValue] = useState("");
  const [modalMessage, setModalMessage] = useState({ type: "", text: "" });
  const [savingEmail, setSavingEmail] = useState(false);
  const [resettingAccess, setResettingAccess] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadUsers = useCallback(
    async (offset = 0) => {
      try {
        setLoading(true);
        setError("");
        const token = await getIdToken();
        const data = await getAdminUsers(token, {
          offset,
          limit: PAGE_SIZE,
          q: query || undefined,
          accountType: accountType || undefined,
        });
        setUsers(data.users || []);
        setPage({
          offset: data.offset ?? offset,
          total: data.total ?? 0,
          has_next: Boolean(data.has_next),
          has_prev: Boolean(data.has_prev),
        });
      } catch (err) {
        setError(err.message || "Não foi possível carregar os usuários.");
      } finally {
        setLoading(false);
      }
    },
    [accountType, getIdToken, query]
  );

  useEffect(() => {
    loadUsers(0);
  }, [loadUsers]);

  const openEditor = (user) => {
    setSelectedUser(user);
    setEmailValue(user.email || "");
    setModalMessage({ type: "", text: "" });
  };

  const closeEditor = () => {
    setSelectedUser(null);
    setModalMessage({ type: "", text: "" });
  };

  const openDetails = async (user) => {
    setDetailsUser(user);
    setBilling(null);
    setBillingError("");
    setBillingLoading(true);
    try {
      const token = await getIdToken();
      const data = await getAdminUserBilling(token, user.user_id);
      setBilling(data);
    } catch (err) {
      setBillingError(err.message || "Não foi possível carregar as assinaturas.");
    } finally {
      setBillingLoading(false);
    }
  };

  const closeDetails = () => {
    setDetailsUser(null);
    setBilling(null);
    setBillingError("");
  };

  const handleEmailSave = async (event) => {
    event.preventDefault();
    if (!selectedUser) return;
    if (!isValidEmail(emailValue)) {
      setModalMessage({ type: "error", text: "Digite um e-mail válido." });
      return;
    }
    try {
      setSavingEmail(true);
      setModalMessage({ type: "", text: "" });
      const token = await getIdToken();
      const result = await updateAdminUserEmail(token, selectedUser.user_id, emailValue.trim());
      setModalMessage({ type: "success", text: result.message || "E-mail alterado com sucesso." });
      setSelectedUser((prev) => (prev ? { ...prev, email: result.email || emailValue.trim() } : prev));
      setUsers((prev) =>
        prev.map((user) =>
          user.user_id === selectedUser.user_id
            ? { ...user, email: result.email || emailValue.trim() }
            : user
        )
      );
    } catch (err) {
      setModalMessage({ type: "error", text: err.message || "Não foi possível alterar o e-mail." });
    } finally {
      setSavingEmail(false);
    }
  };

  const handleResetAccess = async () => {
    if (!selectedUser) return;
    try {
      setResettingAccess(true);
      setModalMessage({ type: "", text: "" });
      const token = await getIdToken();
      const result = await resetAdminUserAccess(token, selectedUser.user_id);
      setModalMessage({
        type: "success",
        text: result.message || "Senha redefinida e e-mail de dados de acesso reenviado.",
      });
    } catch (err) {
      setModalMessage({
        type: "error",
        text: err.message || "Não foi possível redefinir a senha e reenviar o e-mail.",
      });
    } finally {
      setResettingAccess(false);
    }
  };

  return (
    <div className="admin-page">
      <AdminReloadOverlay visible={loading} message="Atualizando usuários..." />
      <div className="admin-page-header">
        <h1 className="admin-title">
          <Users size={30} className="admin-title-icon" />
          Usuários
        </h1>
        <p className="admin-subtitle">
          Lista paginada de contas. Edite o e-mail ou reenvie os dados de acesso.
        </p>
      </div>

      <div className="admin-controls">
        <div className="admin-control admin-control-grow">
          <label className="admin-control-label" htmlFor="admin-users-search">
            Buscar
          </label>
          <input
            id="admin-users-search"
            type="search"
            className="admin-control-input"
            placeholder="Nome, e-mail, slug ou ID..."
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </div>
        <div className="admin-control">
          <label className="admin-control-label" htmlFor="admin-users-type">
            Tipo de conta
          </label>
          <select
            id="admin-users-type"
            className="admin-control-select"
            value={accountType}
            onChange={(event) => setAccountType(event.target.value)}
          >
            <option value="">Todos</option>
            <option value="free">Gratuita</option>
            <option value="paid">Premium</option>
          </select>
        </div>
      </div>

      {error && <div className="admin-error">{error}</div>}
      {!loading && users.length === 0 && (
        <div className="admin-empty">Nenhum usuário encontrado.</div>
      )}

      {users.length > 0 && (
        <div className="admin-panel">
          <div className="admin-table-wrap">
            <table className="admin-table admin-users-table">
              <thead>
                <tr>
                  <th>Usuário</th>
                  <th>E-mail</th>
                  <th>Tipo</th>
                  <th>Score</th>
                  <th>Criado em</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.user_id || user.id}>
                    <td>
                      <div className="admin-user-cell">
                        <img
                          className="admin-user-photo"
                          src={user.profile_photo || defaultPhoto}
                          alt=""
                          onError={(event) => {
                            event.currentTarget.src = defaultPhoto;
                          }}
                        />
                        <div>
                          <strong>{user.full_name || "—"}</strong>
                          <div className="admin-muted">{user.slug ? `@${user.slug}` : "sem slug"}</div>
                        </div>
                      </div>
                    </td>
                    <td>{user.email || "—"}</td>
                    <td>
                      <span
                        className={`admin-status ${
                          user.account_type === "paid" ? "admin-status-paid" : "admin-status-free"
                        }`}
                      >
                        {user.account_type === "paid" ? "Premium" : "Gratuita"}
                      </span>
                    </td>
                    <td>{user.score ?? 0}</td>
                    <td>{formatDateBr(user.created_at)}</td>
                    <td>
                      <div className="admin-icon-actions">
                        <button
                          type="button"
                          className="admin-secondary-btn admin-icon-only-btn"
                          onClick={() => openDetails(user)}
                          aria-label="Ver assinaturas"
                          title="Ver assinaturas"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          type="button"
                          className="admin-secondary-btn admin-icon-only-btn"
                          onClick={() => openEditor(user)}
                          aria-label="Editar"
                          title="Editar"
                        >
                          <Pencil size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <AdminPagination
            offset={page.offset}
            limit={PAGE_SIZE}
            total={page.total}
            itemLabel="usuário"
            visibleCount={users.length}
            disabled={loading}
            onPage={(nextOffset) => loadUsers(nextOffset)}
          />
        </div>
      )}

      {selectedUser && (
        <div className="admin-overlay" onClick={closeEditor}>
          <div className="admin-modal" onClick={(event) => event.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Editar {selectedUser.full_name || "usuário"}</h3>
              <button type="button" className="admin-modal-close" onClick={closeEditor} aria-label="Fechar">
                <X size={20} />
              </button>
            </div>
            <div className="admin-modal-body">
              {modalMessage.text && (
                <div className={modalMessage.type === "success" ? "admin-success" : "admin-error"}>
                  {modalMessage.text}
                </div>
              )}
              <form onSubmit={handleEmailSave}>
                <div className="admin-control">
                  <label className="admin-control-label" htmlFor="admin-user-email">
                    E-mail
                  </label>
                  <input
                    id="admin-user-email"
                    type="email"
                    className="admin-control-input"
                    value={emailValue}
                    onChange={(event) => setEmailValue(event.target.value)}
                  />
                </div>
                <div className="admin-modal-footer" style={{ padding: "12px 0 0" }}>
                  <button type="submit" className="admin-primary-btn" disabled={savingEmail}>
                    {savingEmail ? "Salvando..." : "Salvar e-mail"}
                  </button>
                </div>
              </form>
              <div>
                <p className="admin-control-label">Senha e acesso</p>
                <p className="admin-muted">
                  Redefine a senha para a senha inicial padrão deste e-mail e reenvia o e-mail
                  “Dados de acesso ao site”, o mesmo enviado na compra.
                </p>
                <div className="admin-modal-footer" style={{ padding: "12px 0 0" }}>
                  <button
                    type="button"
                    className="admin-primary-btn"
                    onClick={handleResetAccess}
                    disabled={resettingAccess}
                  >
                    {resettingAccess ? "Enviando..." : "Redefinir senha e reenviar dados de acesso"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {detailsUser && (
        <div className="admin-overlay" onClick={closeDetails}>
          <div className="admin-modal admin-modal-wide" onClick={(event) => event.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>{detailsUser.full_name || "Usuário"}</h3>
              <button type="button" className="admin-modal-close" onClick={closeDetails} aria-label="Fechar">
                <X size={20} />
              </button>
            </div>
            <div className="admin-modal-body">
              {billingLoading && <div className="admin-loading">Carregando assinaturas...</div>}
              {billingError && <div className="admin-error">{billingError}</div>}
              {billing && (
                <>
                  <div className="admin-billing-meta">
                    <div>
                      <span className="admin-control-label">Origem</span>
                      <p>{billing.origin || "—"}</p>
                    </div>
                    <div>
                      <span className="admin-control-label">Tipo</span>
                      <p>
                        {billing.account_type === "paid"
                          ? "Premium"
                          : billing.account_type === "free"
                          ? "Gratuita"
                          : billing.account_type || "—"}
                      </p>
                    </div>
                    <div>
                      <span className="admin-control-label">Acesso</span>
                      <p>
                        {billing.subscription_start_date || billing.subscription_end_date
                          ? `${formatDateBr(billing.subscription_start_date)} até ${formatDateBr(
                              billing.subscription_end_date
                            )}`
                          : "—"}
                      </p>
                    </div>
                  </div>

                  <h4 className="admin-section-title">Assinaturas</h4>
                  {billing.subscriptions?.length === 0 && (
                    <div className="admin-empty">Nenhuma assinatura encontrada.</div>
                  )}
                  {billing.subscriptions?.map((subscription) => (
                    <div key={subscription.subscription_id} className="admin-billing-card">
                      <div className="admin-billing-card-head">
                        <div>
                          <strong>
                            {CYCLE_LABELS[subscription.cycle] || subscription.cycle || "Assinatura"}
                            {" · "}
                            {BILLING_LABELS[subscription.billing_type] ||
                              subscription.billing_type ||
                              "—"}
                          </strong>
                          <div className="admin-muted">
                            {subscription.offer_name || subscription.offer_code || "Sem oferta"}
                            {subscription.provider ? ` · ${subscription.provider}` : ""}
                          </div>
                        </div>
                        <span className={statusClass(subscription.status)}>
                          {SUBSCRIPTION_STATUS_LABELS[subscription.status] ||
                            subscription.status ||
                            "—"}
                        </span>
                      </div>
                      <div className="admin-muted">
                        {formatBRL(subscription.value)}
                        {subscription.created_at
                          ? ` · criada em ${formatDateTimeBr(subscription.created_at)}`
                          : ""}
                        {subscription.external_id ? ` · ${subscription.external_id}` : ""}
                      </div>

                      <p className="admin-control-label" style={{ marginTop: 10 }}>
                        Pagamentos
                      </p>
                      {subscription.payments?.length === 0 ? (
                        <div className="admin-empty">Nenhum pagamento nesta assinatura.</div>
                      ) : (
                        <div className="admin-table-wrap">
                          <table className="admin-table admin-billing-table">
                            <thead>
                              <tr>
                                <th>Data</th>
                                <th>Valor</th>
                                <th>Status</th>
                                <th>ID</th>
                              </tr>
                            </thead>
                            <tbody>
                              {subscription.payments.map((payment) => (
                                <tr key={payment.payment_id}>
                                  <td>{formatDateTimeBr(payment.created_at)}</td>
                                  <td>
                                    {formatBRL(payment.value)}
                                    {payment.num_insts > 1 ? ` · ${payment.num_insts}x` : ""}
                                  </td>
                                  <td>
                                    <span className={statusClass(payment.status)}>
                                      {PAYMENT_LABELS[payment.status] || payment.status || "—"}
                                    </span>
                                    {payment.invoice_url && (
                                      <div>
                                        <a
                                          href={payment.invoice_url}
                                          target="_blank"
                                          rel="noreferrer"
                                        >
                                          Fatura
                                        </a>
                                      </div>
                                    )}
                                  </td>
                                  <td className="admin-muted">{payment.external_id || "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
